import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatEuro, formatDate } from "../lib/format";
import { Input } from "../components/ui/input";
import {
  Search, Receipt, ArrowDownCircle, ArrowUpCircle, AlertCircle, TrendingDown, TrendingUp,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";

const KIND_META = {
  payment: { label: "Payment", color: "#2C4A3B", bg: "#E5EFE7", border: "#829A88" },
  interest: { label: "Interest", color: "#C89B6A", bg: "#FCF6E9", border: "#E5DCC5" },
  extra_charge: { label: "Extra charge", color: "#B9762E", bg: "#FAEEE5", border: "#E07A5F" },
};

const KIND_ICON = {
  payment: ArrowDownCircle,
  interest: ArrowUpCircle,
  extra_charge: AlertCircle,
};

const formatMonthShort = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

export default function ActivityPage() {
  const [activity, setActivity] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [whoFilter, setWhoFilter] = useState("all");
  const [cashflowRange, setCashflowRange] = useState("12");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/activity"),
        api.get("/cashflow", { params: { months: cashflowRange } }),
      ]);
      setActivity(a.data);
      setCashflow(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [cashflowRange]);

  const events = activity?.events || [];

  const people = useMemo(() => {
    const set = new Set();
    for (const e of events) if (e.debt_who) set.add(e.debt_who);
    return Array.from(set).sort();
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (kindFilter !== "all" && e.kind !== kindFilter) return false;
      if (whoFilter !== "all" && e.debt_who !== whoFilter) return false;
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return [e.debt_code, e.debt_owed_to, e.debt_who, e.notes, KIND_META[e.kind]?.label]
        .some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [events, kindFilter, whoFilter, q]);

  const filteredTotals = useMemo(() => {
    let pay = 0, intr = 0, extra = 0;
    for (const e of filtered) {
      if (e.kind === "payment") pay += e.amount;
      else if (e.kind === "interest") intr += e.amount;
      else if (e.kind === "extra_charge") extra += e.amount;
    }
    return { pay, intr, extra, net: intr + extra - pay };
  }, [filtered]);

  return (
    <div className="space-y-8 fade-in">
      <div>
        <p className="label-kicker">Ledger</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
          Activity & cashflow
        </h1>
        <p className="text-[#5C635A] mt-2 max-w-2xl">
          Every event that moved your debts — payments shrink them, interest and extra charges grow
          them. See it month by month, then drill into each entry.
        </p>
      </div>

      {/* Top totals */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-5 stagger">
        <SummaryTile
          icon={<ArrowDownCircle className="w-4 h-4" />}
          label="Payments (all time)"
          value={formatEuro(activity?.totals?.payments || 0)}
          accent="#2C4A3B"
          testId="activity-total-payments"
        />
        <SummaryTile
          icon={<ArrowUpCircle className="w-4 h-4" />}
          label="Interest added"
          value={formatEuro(activity?.totals?.interest || 0)}
          accent="#C89B6A"
        />
        <SummaryTile
          icon={<AlertCircle className="w-4 h-4" />}
          label="Extra charges added"
          value={formatEuro(activity?.totals?.extra_charge || 0)}
          accent="#B9762E"
        />
        <SummaryTile
          icon={cashflow?.totals?.net_debt_change <= 0
            ? <TrendingDown className="w-4 h-4" />
            : <TrendingUp className="w-4 h-4" />}
          label="Net debt change (period)"
          value={formatEuro(cashflow?.totals?.net_debt_change || 0)}
          sub="Negative = paying down"
          accent={cashflow?.totals?.net_debt_change <= 0 ? "#2C4A3B" : "#B04A32"}
          testId="cashflow-net-change"
        />
      </section>

      {/* Cashflow chart */}
      <section className="card-soft p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="label-kicker">Monthly cashflow</p>
            <h3 className="font-display text-xl text-[#1D3328] mt-1">
              Payments out vs additions in
            </h3>
          </div>
          <Tabs value={cashflowRange} onValueChange={setCashflowRange}>
            <TabsList className="bg-[#EEEBE2]" data-testid="cashflow-range-tabs">
              <TabsTrigger value="6" className="data-[state=active]:bg-white" data-testid="cashflow-range-6">6 mo</TabsTrigger>
              <TabsTrigger value="12" className="data-[state=active]:bg-white" data-testid="cashflow-range-12">12 mo</TabsTrigger>
              <TabsTrigger value="24" className="data-[state=active]:bg-white" data-testid="cashflow-range-24">24 mo</TabsTrigger>
              <TabsTrigger value="120" className="data-[state=active]:bg-white" data-testid="cashflow-range-all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {loading && !cashflow ? (
          <div className="h-72 flex items-center justify-center text-[#8B9389] text-xs">Loading…</div>
        ) : (cashflow?.months || []).length === 0 ? (
          <p className="text-[#8B9389] text-sm">No activity to chart yet.</p>
        ) : (
          <>
            <div className="h-72 min-h-[18rem] w-full" data-testid="cashflow-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashflow.months} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                  <XAxis dataKey="month" tickFormatter={formatMonthShort} tick={{ fontSize: 11, fill: "#8B9389" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#8B9389" }} tickFormatter={(v) => `€${Math.round(v)}`} />
                  <Tooltip
                    formatter={(v, name) => [formatEuro(v), name]}
                    labelFormatter={(l) => formatMonthShort(l)}
                    contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                  />
                  <ReferenceLine y={0} stroke="#C5CFC3" />
                  <Legend />
                  <Bar dataKey="payments_out" name="Payments out" stackId="out" fill="#2C4A3B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="interest_added" name="Interest" stackId="in" fill="#C89B6A" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="extra_charges_added" name="Extra charges" stackId="in" fill="#E07A5F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto -mx-2 mt-6">
              <table className="w-full text-sm" data-testid="cashflow-table">
                <thead>
                  <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                    <th className="py-3 px-2">Month</th>
                    <th className="py-3 px-2 text-right">Payments out</th>
                    <th className="py-3 px-2 text-right">Interest</th>
                    <th className="py-3 px-2 text-right">Extra charges</th>
                    <th className="py-3 px-2 text-right">Net debt change</th>
                  </tr>
                </thead>
                <tbody>
                  {[...cashflow.months].reverse().map((row) => (
                    <tr key={row.month} className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60" data-testid="cashflow-row">
                      <td className="py-3 px-2 text-[#1D3328] font-medium">{formatMonthShort(row.month)}</td>
                      <td className="py-3 px-2 text-right num text-[#2C4A3B]">−{formatEuro(row.payments_out)}</td>
                      <td className="py-3 px-2 text-right num text-[#C89B6A]">+{formatEuro(row.interest_added)}</td>
                      <td className="py-3 px-2 text-right num text-[#E07A5F]">+{formatEuro(row.extra_charges_added)}</td>
                      <td
                        className="py-3 px-2 text-right num font-medium"
                        style={{ color: row.net_debt_change <= 0 ? "#2C4A3B" : "#B04A32" }}
                      >
                        {row.net_debt_change >= 0 ? "+" : ""}{formatEuro(row.net_debt_change)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#E5E3DB]">
                    <td className="py-3 px-2 label-kicker text-[#1D3328]">Period total</td>
                    <td className="py-3 px-2 text-right num text-[#2C4A3B]">−{formatEuro(cashflow.totals.payments_out)}</td>
                    <td className="py-3 px-2 text-right num text-[#C89B6A]">+{formatEuro(cashflow.totals.interest_added)}</td>
                    <td className="py-3 px-2 text-right num text-[#E07A5F]">+{formatEuro(cashflow.totals.extra_charges_added)}</td>
                    <td
                      className="py-3 px-2 text-right num font-medium"
                      style={{ color: cashflow.totals.net_debt_change <= 0 ? "#2C4A3B" : "#B04A32" }}
                      data-testid="cashflow-period-net"
                    >
                      {cashflow.totals.net_debt_change >= 0 ? "+" : ""}{formatEuro(cashflow.totals.net_debt_change)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Unified activity feed */}
      <section className="card-soft p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="label-kicker">Every event</p>
            <h3 className="font-display text-xl text-[#1D3328] mt-1 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#2C4A3B]" strokeWidth={1.7} /> Unified activity feed
            </h3>
          </div>
          <span className="text-xs text-[#8B9389]">{filtered.length} of {events.length}</span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
          <Tabs value={kindFilter} onValueChange={setKindFilter}>
            <TabsList className="bg-[#EEEBE2]" data-testid="activity-kind-filter">
              <TabsTrigger value="all" className="data-[state=active]:bg-white" data-testid="activity-filter-all">
                All
              </TabsTrigger>
              <TabsTrigger value="payment" className="data-[state=active]:bg-white data-[state=active]:text-[#2C4A3B]" data-testid="activity-filter-payment">
                Payments
              </TabsTrigger>
              <TabsTrigger value="interest" className="data-[state=active]:bg-white data-[state=active]:text-[#C89B6A]" data-testid="activity-filter-interest">
                Interest
              </TabsTrigger>
              <TabsTrigger value="extra_charge" className="data-[state=active]:bg-white data-[state=active]:text-[#B9762E]" data-testid="activity-filter-extra">
                Extra
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {people.length > 0 && (
            <Tabs value={whoFilter} onValueChange={setWhoFilter}>
              <TabsList className="bg-[#EEEBE2]" data-testid="activity-who-filter">
                <TabsTrigger value="all" className="data-[state=active]:bg-white" data-testid="activity-who-all">All</TabsTrigger>
                {people.map((w) => (
                  <TabsTrigger key={w} value={w} className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]">
                    {w}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
          <div className="relative flex-1 max-w-md ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B9389]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code, creditor, person, notes…"
              className="pl-9 rounded-xl border-[#E5E3DB] bg-white"
              data-testid="activity-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs py-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[#5C635A]">
              {events.length === 0 ? "No activity recorded yet." : "No matches for your filters."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead>
                <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                  <th className="py-3 px-2">Date</th>
                  <th className="py-3 px-2">Type</th>
                  <th className="py-3 px-2">Debt</th>
                  <th className="py-3 px-2">Who</th>
                  <th className="py-3 px-2">Notes</th>
                  <th className="py-3 px-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const meta = KIND_META[e.kind] || KIND_META.payment;
                  const Icon = KIND_ICON[e.kind] || ArrowDownCircle;
                  const sign = e.sign < 0 ? "−" : "+";
                  return (
                    <tr key={e.id} className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60" data-testid="activity-row">
                      <td className="py-3 px-2 text-[#5C635A] whitespace-nowrap">{formatDate(e.date)}</td>
                      <td className="py-3 px-2">
                        <span
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-bold rounded-md px-2 py-1 border"
                          style={{ background: meta.bg, borderColor: meta.border, color: meta.color }}
                          data-testid="activity-kind-badge"
                        >
                          <Icon className="w-3 h-3" strokeWidth={1.8} />
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <Link to={`/debts/${e.debt_id}`} className="block">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] text-[#8B9389]">{e.debt_code}</span>
                            <span className="font-medium text-[#1D3328]">{e.debt_owed_to}</span>
                          </div>
                        </Link>
                      </td>
                      <td className="py-3 px-2 text-[#5C635A]">{e.debt_who}</td>
                      <td className="py-3 px-2 text-[#5C635A] max-w-md">
                        <span className="line-clamp-2">{e.notes || "—"}</span>
                      </td>
                      <td
                        className="py-3 px-2 text-right num font-medium whitespace-nowrap"
                        style={{ color: meta.color }}
                      >
                        {sign} {formatEuro(e.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#E5E3DB]">
                  <td colSpan={5} className="py-3 px-2 text-right label-kicker text-[#1D3328]">
                    Filtered net change
                  </td>
                  <td
                    className="py-3 px-2 text-right num font-medium whitespace-nowrap"
                    style={{ color: filteredTotals.net <= 0 ? "#2C4A3B" : "#B04A32" }}
                    data-testid="activity-filtered-net"
                  >
                    {filteredTotals.net >= 0 ? "+" : ""}{formatEuro(filteredTotals.net)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryTile({ icon, label, value, sub, accent = "#1D3328", testId }) {
  return (
    <div className="card-soft p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="label-kicker">{label}</p>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}18`, color: accent }}
        >
          {icon}
        </div>
      </div>
      <div className="font-display text-2xl font-medium num text-[#1A1C1A] mt-3">{value}</div>
      {sub && <div className="text-xs text-[#8B9389] mt-1">{sub}</div>}
    </div>
  );
}
